import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';

import ContextStorage from './context';

// setState 入参是否为函数
function isFn<S>(newState: SetStateAction<S>): newState is (prevState: S) => S {
  return typeof newState === 'function';
}

// 不要为此 hook 添加 initValue 参数，手动 setState 会安全很多
function useStorage<S = any>(key: string): [S | undefined, Dispatch<SetStateAction<S>>] {
  const storage = useContext(ContextStorage);
  const [value, steValue] = useState<S | undefined>(storage.get(key));

  const setStorage = useCallback<Dispatch<SetStateAction<S>>>((newValue) => {
    const oldValue = storage.get(key);
    const _newValue = isFn(newValue) ? newValue(oldValue) : newValue;

    if (oldValue !== _newValue) {
      storage.emit(key, _newValue);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const currentValue = storage.get(key);

    storage.on(key, steValue);

    // 上面 useState 给出的初始值是立即执行的，但 storage.on 是在 hook 中的。
    // 期间可能会有其他 hook （特别是上个组件的 unMount hook）中发出的 storage.emit 未被接收，所以需要在绑定时检查是否需要更新。
    if (value !== currentValue) {
      steValue(currentValue);
    }

    return () => {
      storage.off(key, steValue);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return [value, setStorage];
}

export default useStorage;
